import React, { useState, useEffect } from "react";
import {
  Navbar,
  Container,
  Nav,
  Button,
  Image,
  Dropdown,
} from "react-bootstrap";
import {
  FaUser,
  FaCog,
  FaRocket,
  FaBars,
  FaPalette,
  FaSignOutAlt,
} from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useUserProfiles } from "../contexts/UserProfileContext";
import UserProfile from "./UserProfile";
import AccountSettings from "./AccountSettings";
import LoginModal from "./LoginModal";
import "./NavBar.css";

const NavBar = () => {
  const [showProfiles, setShowProfiles] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { user, signInWithGoogle, logOut } = useUser();
  const {
    activeProfile,
    profiles,
    isLoading: profilesLoading,
  } = useUserProfiles();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle auth state changes
  useEffect(() => {
    console.log("NavBar auth state changed:", {
      user,
      loading: isLoading,
      path: location.pathname,
      hasActiveProfile: !!activeProfile,
      profilesLength: profiles.length,
      profilesLoading,
    });

    // Only redirect if we have a user and we're at the root path
    // But don't interfere with new user flow - let SignIn component handle that
    if (user && location.pathname === "/" && !profilesLoading) {
      if (profiles.length === 0) {
        console.log(
          "User logged in at root path with no profiles, redirecting to create-profile"
        );
        // Small delay to ensure auth state is fully processed
        const redirectTimer = setTimeout(() => {
          navigate("/create-profile");
        }, 100);
        return () => clearTimeout(redirectTimer);
      } else if (activeProfile) {
        console.log(
          "User logged in at root path with active profile, redirecting to /games"
        );
        // Small delay to ensure auth state is fully processed
        const redirectTimer = setTimeout(() => {
          navigate("/games");
        }, 100);
        return () => clearTimeout(redirectTimer);
      }
    }
  }, [
    user,
    location.pathname,
    navigate,
    isLoading,
    activeProfile,
    profiles,
    profilesLoading,
  ]);

  const handleGoogleSignIn = async () => {
    console.log("Starting Google Sign In in NavBar...");
    setError("");
    setIsLoading(true);
    try {
      console.log("Calling signInWithGoogle...");
      const result = await signInWithGoogle();
      console.log("Sign in result:", result);
      if (result.success) {
        console.log("Login successful, closing modal");
        setShowLoginModal(false);
        // Navigation will be handled by the useEffect hook after auth state changes
      } else {
        console.log("Login failed:", result.error);
        setError(result.error);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message);
    } finally {
      console.log("Sign in process complete");
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logOut();
      navigate("/games");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const renderAuthButtons = () => {
    if (user) {
      return (
        <>
          <div className="d-none d-lg-flex align-items-center">
            <Button
              variant="outline-light"
              onClick={() => setShowProfiles(true)}
              className="profile-button text-white"
            >
              {activeProfile?.avatar ? (
                <Image
                  src={activeProfile.avatar}
                  roundedCircle
                  className="profile-avatar"
                  alt="Profile"
                  width={24}
                  height={24}
                />
              ) : (
                <FaUser className="me-2" />
              )}
              <span className="profile-name text-white">
                {!profiles.length
                  ? "Create Profile"
                  : activeProfile?.name || "Select Profile"}
              </span>
            </Button>
          </div>
          <Button
            variant="outline-light"
            onClick={handleLogout}
            className="logout-button d-none d-lg-flex"
          >
            Logout
          </Button>
          <Button
            variant="outline-light"
            onClick={() => setShowSettings(true)}
            className="settings-button d-none d-lg-flex"
          >
            <FaCog />
          </Button>

          {/* Mobile Menu */}
          <Dropdown className="d-lg-none">
            <Dropdown.Toggle
              variant="outline-light"
              id="mobile-menu"
              className="mobile-menu-toggle"
            >
              {activeProfile && (
                <>
                  {activeProfile.avatar && (
                    <Image
                      src={activeProfile.avatar}
                      roundedCircle
                      className="mobile-avatar me-2"
                      alt="Profile"
                      width={24}
                      height={24}
                    />
                  )}
                  <span className="mobile-profile-name me-2">
                    {activeProfile.name}
                  </span>
                </>
              )}
              <FaBars />
            </Dropdown.Toggle>
            <Dropdown.Menu align="end" className="mobile-menu">
              <Dropdown.Item
                href="/games"
                className="d-flex align-items-center gap-2"
              >
                <FaRocket style={{ transform: "rotate(-45deg)" }} />
                Home
              </Dropdown.Item>
              {user && activeProfile && (
                <Dropdown.Item
                  href="/games"
                  className="d-flex align-items-center gap-2"
                >
                  <FaUser />
                  Games
                </Dropdown.Item>
              )}
              {user && (
                <Dropdown.Item
                  href="/coloring-pages"
                  className="d-flex align-items-center gap-2"
                >
                  <FaPalette />
                  Coloring Pages
                </Dropdown.Item>
              )}
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => setShowProfiles(true)}
                className="d-flex align-items-center gap-2"
              >
                <FaUser />
                {!profiles.length
                  ? "Create Profile"
                  : activeProfile?.name || "Select Profile"}
              </Dropdown.Item>
              <Dropdown.Item
                onClick={() => setShowSettings(true)}
                className="d-flex align-items-center gap-2"
              >
                <FaCog />
                Settings
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={handleLogout}
                className="d-flex align-items-center gap-2"
              >
                <FaSignOutAlt />
                Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </>
      );
    }

    return (
      <Button
        variant="outline-light"
        onClick={() => setShowLoginModal(true)}
        className="login-button"
      >
        Sign In
      </Button>
    );
  };

  return (
    <>
      <Navbar bg="dark" variant="dark" expand="lg" fixed="top">
        <Container>
          <Navbar.Brand
            href="/games"
            className="text-white d-flex align-items-center"
          >
            <FaRocket
              className="brand-icon me-2"
              style={{ transform: "rotate(-45deg)" }}
            />
            Star Launch
          </Navbar.Brand>
          <Nav className="me-auto d-none d-lg-flex">
            {user && activeProfile && (
              <Nav.Link href="/games" className="text-white">
                Bring Your Play to Life
              </Nav.Link>
            )}
            {user && (
              <Nav.Link href="/coloring-pages" className="text-white">
                Coloring Pages
              </Nav.Link>
            )}
          </Nav>
          <div className="d-flex align-items-center gap-3">
            {renderAuthButtons()}
          </div>
        </Container>
      </Navbar>

      {/* Add spacing to prevent content from hiding under fixed navbar */}
      <div className="navbar-spacer" />

      {/* Login Modal */}
      <LoginModal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        error={error}
        onGoogleSignIn={handleGoogleSignIn}
        isLoading={isLoading}
      />

      {/* Other Modals */}
      {user && (
        <>
          <UserProfile
            show={showProfiles}
            onHide={() => setShowProfiles(false)}
          />
          <AccountSettings
            show={showSettings}
            onHide={() => setShowSettings(false)}
          />
        </>
      )}
    </>
  );
};

export default NavBar;
